import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface VideoProgress {
  video_id: string;
  completed: boolean;
  completed_at?: string;
}

interface UseVideoProgressProps {
  userId?: string;
  initialVideos: Array<{ id: string; completed: boolean }>;
}

export function useVideoProgress({ userId, initialVideos }: UseVideoProgressProps) {
  const [videoProgress, setVideoProgress] = useState<Record<string, boolean>>(() => {
    // Initialize from initialVideos
    const initial: Record<string, boolean> = {};
    initialVideos.forEach(video => {
      initial[video.id] = video.completed;
    });
    return initial;
  });

  const [isLoading, setIsLoading] = useState(true);

  // Load progress from localStorage and database
  const loadProgress = useCallback(async () => {
    if (!userId || userId === 'anonymous') {
      console.log('⚠️ No valid userId, using local storage only');
      // Still try to load from localStorage for anonymous users
      try {
        const localStorageKey = `videoProgress_${userId || 'anonymous'}`;
        const localProgress = localStorage.getItem(localStorageKey);
        if (localProgress) {
          const parsed = JSON.parse(localProgress);
          setVideoProgress(prev => ({ ...prev, ...parsed }));
        }
      } catch (error) {
        console.error('❌ Error loading from localStorage:', error);
      }
      setIsLoading(false);
      return;
    }

    try {
      // Load from localStorage first for immediate UI update
      const localStorageKey = `videoProgress_${userId}`;
      const localProgress = localStorage.getItem(localStorageKey);
      if (localProgress) {
        const parsed = JSON.parse(localProgress);
        setVideoProgress(prev => ({ ...prev, ...parsed }));
      }

      // Then load from database for accurate data
      const { data: progressData, error } = await supabase
        .from('video_progress')
        .select('video_id, completed, completed_at')
        .eq('user_id', userId);

      if (error) {
        console.error('Error loading video progress:', error);
        setIsLoading(false);
        return;
      }

      if (progressData && progressData.length > 0) {
        const dbProgress: Record<string, boolean> = {};
        progressData.forEach((item: VideoProgress) => {
          dbProgress[item.video_id] = item.completed;
        });

        setVideoProgress(prev => ({ ...prev, ...dbProgress }));
        
        // Update localStorage with database data
        localStorage.setItem(localStorageKey, JSON.stringify(dbProgress));
      }
    } catch (error) {
      console.error('Error in loadProgress:', error);
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  // Save progress to both localStorage and database
  const updateVideoProgress = useCallback(async (videoId: string, completed: boolean) => {
    console.log('🎯 updateVideoProgress called:', { videoId, completed, userId });
    
    if (!userId) {
      console.error('❌ No userId provided');
      return { success: false, error: 'No user ID' };
    }

    // For anonymous users, only save to localStorage
    if (userId === 'anonymous') {
      console.log('📝 Anonymous user, saving to localStorage only');
      const newProgress = { ...videoProgress, [videoId]: completed };
      setVideoProgress(newProgress);
      
      const localStorageKey = `videoProgress_anonymous`;
      localStorage.setItem(localStorageKey, JSON.stringify(newProgress));
      console.log('💾 Saved to localStorage for anonymous user');
      return { success: true };
    }

    console.log('📝 Current videoProgress:', videoProgress);
    
    const newProgress = { ...videoProgress, [videoId]: completed };
    console.log('📝 New progress state:', newProgress);
    
    setVideoProgress(newProgress);

    // Save to localStorage immediately
    const localStorageKey = `videoProgress_${userId}`;
    localStorage.setItem(localStorageKey, JSON.stringify(newProgress));
    console.log('💾 Saved to localStorage:', localStorageKey);

    try {
      console.log('🚀 Starting database save...');
      
      if (completed) {
        const upsertData = {
          user_id: userId,
          video_id: videoId,
          completed,
          completed_at: new Date().toISOString()
        };
        
        console.log('📡 Upsert data:', upsertData);
        
        // Save to database
        const { data, error } = await supabase
          .from('video_progress')
          .upsert(upsertData, {
            onConflict: 'user_id,video_id'
          })
          .select();

        console.log('🔍 Database response:', { data, error });

        if (error) {
          console.error('❌ Database error:', error);
          console.error('❌ Error details:', {
            message: error.message,
            details: error.details,
            hint: error.hint,
            code: error.code
          });
          
          // Revert localStorage if database save fails
          const revertedProgress = { ...videoProgress };
          setVideoProgress(revertedProgress);
          localStorage.setItem(localStorageKey, JSON.stringify(revertedProgress));
          
          return { success: false, error: error.message };
        } else {
          console.log('✅ Successfully saved video progress:', videoId, 'completed:', completed);
          console.log('✅ Database returned:', data);
          return { success: true };
        }
      } else {
        // Unmark video - delete from database
        console.log('🗑️ Unmarking video:', videoId);
        
        const { error } = await supabase
          .rpc('unmark_video_progress', {
            p_user_id: userId,
            p_video_id: videoId
          });

        console.log('🔍 Unmark response:', { error });

        if (error) {
          console.error('❌ Database error:', error);
          
          // Revert localStorage if database save fails
          const revertedProgress = { ...videoProgress };
          setVideoProgress(revertedProgress);
          localStorage.setItem(localStorageKey, JSON.stringify(revertedProgress));
          
          return { success: false, error: error.message };
        } else {
          console.log('✅ Successfully unmarked video progress:', videoId);
          return { success: true };
        }
      }
    } catch (error) {
      console.error('💥 Exception in updateVideoProgress:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }, [userId, videoProgress]);

  // Reset all progress
  const resetAllProgress = useCallback(async () => {
    if (!userId) {
      return { success: false, error: 'No user ID' };
    }

    // For anonymous users, only clear localStorage
    if (userId === 'anonymous') {
      const localStorageKey = `videoProgress_anonymous`;
      localStorage.removeItem(localStorageKey);
      setVideoProgress({});
      console.log('✅ Reset progress for anonymous user');
      return { success: true };
    }

    try {
      console.log('🔄 Resetting all progress for user:', userId);
      
      const { error } = await supabase
        .rpc('reset_user_video_progress', {
          p_user_id: userId
        });

      if (error) {
        console.error('❌ Reset error:', error);
        return { success: false, error: error.message };
      }

      // Clear localStorage
      const localStorageKey = `videoProgress_${userId}`;
      localStorage.removeItem(localStorageKey);
      
      // Reset state
      setVideoProgress({});
      
      console.log('✅ Successfully reset all progress');
      return { success: true };
    } catch (error) {
      console.error('💥 Exception in resetAllProgress:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }, [userId]);

  // Load progress on mount
  useEffect(() => {
    loadProgress();
  }, [loadProgress]);

  return {
    videoProgress,
    updateVideoProgress,
    resetAllProgress,
    isLoading
  };
}